/** Invitation redemption through the institution's Internal Google client. */
import { PUBLIC_LINK } from "@/components/public/control-classes";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { AuthCard } from "@/components/auth/AuthCard";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantPresentation } from "@/hooks/use-tenant-presentation";
import { ApiError } from "@/lib/api/client";
import { invitationsApi } from "@/lib/api/invitations";
import { ACTIVATED_EMAIL_KEY, takeTokenFromFragment } from "@/lib/activation-token";
import { authFrontDoorForHostname } from "@/lib/supabase/auth-front-door";

const TOKEN_KEY = `imam-lms-invitation-token:${window.location.hostname}`;

const ACTIVATE_FRAME = {
  eyebrow: "Account invitation",
  heading: "Activate your access",
  subtitle: "Activate your access",
  lede: "Continue with the managed Google account created for this invitation.",
} as const;

export function ActivatePage() {
  const [token] = useState(() => {
    const fragmentToken = takeTokenFromFragment();
    if (fragmentToken) {
      window.sessionStorage.setItem(TOKEN_KEY, fragmentToken);
      return fragmentToken;
    }
    return window.sessionStorage.getItem(TOKEN_KEY) ?? "";
  });
  const { name, logoUrl } = useTenantPresentation();
  const { session, signInWithGoogle, signOut, refreshMembership } = useAuth();
  const activationState = authFrontDoorForHostname()?.activation ?? "preparing";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [startingGoogle, setStartingGoogle] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fullName, setFullName] = useState("");

  const peek = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => invitationsApi.peek(token),
    enabled: token.length > 0,
    retry: false,
  });

  const redeem = useMutation({
    mutationFn: () => invitationsApi.redeem(token),
    onSuccess: async () => {
      // The durable copy is dropped here, and on this path only here: a redeemed
      // invitation is spent, so nothing live is left behind. It deliberately
      // survives a *failed* attempt: see the redemption effect below.
      window.sessionStorage.removeItem(TOKEN_KEY);
      queryClient.clear();
      // The OAuth session arrived before redemption, so its first /me read
      // correctly found no membership. Refresh after the atomic redemption or
      // the app would keep that pre-activation 403 until a page reload.
      await refreshMembership();
      navigate("/", { replace: true });
    },
    onError: (failure) => {
      setError(
        failure instanceof ApiError
          ? failure.message
          : "The invitation could not be activated.",
      );
      // A refusal never mutates the invitation: every non-ok outcome of
      // lms_redeem_provisioned_invitation returns before it writes. So the
      // link's liveness after a failure is exactly what a fresh peek reports,
      // and re-reading it is how this page distinguishes a terminal refusal
      // from a recoverable one without classifying server prose. Used, revoked
      // or expired comes back unusable and falls to the branch below, which
      // states the real reason and drops the token. A wrong Google account
      // leaves the invitation issued, so peek keeps saying usable, the token
      // stays, and the same link still redeems for the right account.
      void queryClient.invalidateQueries({ queryKey: ["invitation", token] });
    },
  });

  const activate = useMutation({
    mutationFn: () => invitationsApi.activate({
      token,
      password,
      full_name: fullName.trim() || null,
    }),
    onSuccess: () => {
      window.sessionStorage.removeItem(TOKEN_KEY);
      if (peek.data?.usable) {
        window.sessionStorage.setItem(ACTIVATED_EMAIL_KEY, peek.data.email);
      }
      navigate("/sign-in?activated=1", { replace: true });
    },
    onError: (failure) => setError(
      failure instanceof ApiError
        ? failure.message
        : "The account could not be activated.",
    ),
  });

  useEffect(() => {
    if (
      session
      && peek.data?.usable
      && peek.data.redeem_with === "google"
      && !redeem.isPending
      && !redeem.isSuccess
      && !error
      && activationState === "ready"
    ) {
      // The token crossed the OAuth redirect in sessionStorage only, and it has
      // already been taken out of the fragment. Clearing it here spent it on an
      // attempt that can legitimately fail: a refused redemption then left no
      // token in the URL and none in storage, so any reload of a still-valid
      // invitation rendered the no-token refusal. It is cleared on success, and
      // on a peek that proves the invitation is finished.
      redeem.mutate();
    }
  }, [activationState, error, peek.data, redeem, session]);

  if (!token) {
    // Nothing here can tell "no token was supplied" from "the token was spent on
    // an attempt that failed", so this must not pronounce the link dead. Both
    // cases are answered by the invitation email.
    return (
      <Frame name={name} logoUrl={logoUrl}>
        <Refusal
          reason="Open the activation link from your invitation email to continue."
          hint="This page needs that link to know which invitation to activate. If an earlier attempt did not go through, the link in that email still works."
        />
      </Frame>
    );
  }
  if (peek.isPending || redeem.isPending || activate.isPending) {
    return (
      <Frame name={name} logoUrl={logoUrl}>
        {/* The shape of what follows (the account line, its explanation, the
            one act) stands in while the invitation is read, so the card does
            not jump; the status line carries the wait. */}
        <div className="grid gap-4" aria-busy="true">
          <p role="status" className="flex items-center gap-2 text-ui text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            {redeem.isPending || activate.isPending ? "Activating your membership" : "Checking your invitation"}
          </p>
          <Skeleton className="h-16" aria-hidden="true" />
          <Skeleton className="h-10 w-4/5" aria-hidden="true" />
          <Skeleton className="h-[var(--control-height)]" aria-hidden="true" />
        </div>
      </Frame>
    );
  }
  if (peek.isError) {
    return (
      <Frame name={name} logoUrl={logoUrl}>
        <div className="grid justify-items-center gap-4 text-center">
          <p className="text-ui text-foreground">Your invitation could not be checked just now.</p>
          <Button variant="outline" onClick={() => void peek.refetch()}>Try again</Button>
        </div>
      </Frame>
    );
  }
  if (!peek.data?.usable) {
    window.sessionStorage.removeItem(TOKEN_KEY);
    return <Frame name={name} logoUrl={logoUrl}><Refusal reason={peek.data?.reason ?? "This activation link is not valid."} /></Frame>;
  }
  const invitedEmail = peek.data.email;
  if (peek.data.redeem_with === "google" && activationState === "preparing") {
    return (
      <Frame name={peek.data.tenant_name || name} logoUrl={logoUrl}>
        <div className="grid gap-2 text-center">
          <p className="text-ui text-foreground">Activation is being prepared.</p>
          <p className="text-ui text-muted-foreground">
            Keep this invitation link and retry only after the office confirms readiness.
          </p>
        </div>
      </Frame>
    );
  }
  if (error && peek.data.redeem_with === "google") {
    return (
      <Frame name={peek.data.tenant_name || name} logoUrl={logoUrl}>
        <div className="grid justify-items-center gap-4">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          {session && (
            <Button
              variant="outline"
              disabled={startingGoogle}
              onClick={() => {
                setStartingGoogle(true);
                // No restore needed: a refused redemption leaves the durable
                // token in place precisely so this redirect, and a plain reload,
                // both still have it.
                void signOut().then(async () => {
                  const result = await signInWithGoogle(
                    `${window.location.origin}/activate`,
                    { selectAccount: true, loginHint: invitedEmail },
                  );
                  if (result.error) {
                    setError(result.error);
                    setStartingGoogle(false);
                  }
                });
              }}
            >
              {startingGoogle && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              Try another Google account
            </Button>
          )}
        </div>
      </Frame>
    );
  }

  if (peek.data.redeem_with === "password") {
    const onSubmit = (event: React.FormEvent) => {
      event.preventDefault();
      setError(null);
      if (password.length < 8) {
        setError("Password must be at least 8 characters.");
        return;
      }
      if (password !== confirm) {
        setError("Passwords do not match.");
        return;
      }
      activate.mutate();
    };
    return (
      <Frame name={peek.data.tenant_name || name} logoUrl={logoUrl}>
        <form onSubmit={onSubmit} className="grid gap-4" noValidate>
          <div className="grid gap-2">
            <Label htmlFor="activate-email">Email</Label>
            <Input id="activate-email" value={peek.data.email} readOnly disabled />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="activate-name">Your name</Label>
            <Input id="activate-name" value={fullName} onChange={(event) => setFullName(event.target.value)} autoComplete="name" maxLength={200} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="activate-password">Password</Label>
            <Input id="activate-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" required minLength={8} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="activate-confirm">Confirm password</Label>
            <Input id="activate-confirm" type="password" value={confirm} onChange={(event) => setConfirm(event.target.value)} autoComplete="new-password" required minLength={8} />
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Button type="submit" className="w-full" disabled={activate.isPending}>
            Activate my account
          </Button>
        </form>
      </Frame>
    );
  }

  return (
    <Frame name={peek.data.tenant_name || name} logoUrl={logoUrl}>
      <div className="grid gap-4">
        <div className="rounded-control bg-well px-4 py-3">
          <div className="eyebrow">Managed account</div>
          <div className="mt-1 break-words font-medium text-foreground">{peek.data.email}</div>
        </div>
        <p className="text-ui text-muted-foreground">
          Continue with this exact account. A different Google account cannot redeem the invitation, and Google account ownership alone grants no LMS role.
        </p>
        <p className="text-ui text-muted-foreground">
          If Google sent you a separate password-setup email, complete it first and
          then return to this page. Google does not return you to IMAM automatically.
          Do not start password recovery from this page.
        </p>
        <Button
          className="w-full"
          disabled={startingGoogle}
          onClick={async () => {
            setStartingGoogle(true);
            const result = await signInWithGoogle(
              `${window.location.origin}/activate`,
              { loginHint: invitedEmail },
            );
            if (result.error) {
              setError(result.error);
              setStartingGoogle(false);
            }
          }}
        >
          {startingGoogle && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Continue with Google
        </Button>
      </div>
    </Frame>
  );
}

function Frame({ name, logoUrl, children }: { name: string; logoUrl: string | null; children: React.ReactNode }) {
  return <AuthCard {...ACTIVATE_FRAME} brandName={name} logoUrl={logoUrl}>{children}</AuthCard>;
}

function Refusal({ reason, hint }: { reason: string; hint?: string }) {
  return (
    <div className="grid justify-items-center gap-4 text-center">
      <div className="grid gap-2">
        <p className="text-ui text-foreground">{reason}</p>
        {hint && <p className="text-ui text-muted-foreground">{hint}</p>}
      </div>
      <Link to="/sign-in" className={`${PUBLIC_LINK} text-ui text-primary hover:underline`}>
        Go to sign in
      </Link>
    </div>
  );
}
